import React, { useState } from 'react';
import isEmpty from 'lodash/fp/isEmpty';
import { connect } from 'unistore/react';
import { actions } from '../store/PlanStore';

import ProductTile from 'common/components/shop/ProductTile';
import ProductListing from 'common/components/shop/ProductListing';
import PriceSpan from 'common/components/formatters/PriceSpan';
import ShopButton from 'common/components/ShopButton';
import QuantityInput from 'common/components/QuantityInput';
import PodsFilter from './PodsFilter';
import LoadingSplash from 'common/components/LoadingSplash';
import { TranslatedMessage } from 'common/components/Intl';
import FlavorGuideModal from './FlavorGuideModal';
import CartErrorModal from './CartErrorModal';
import RemovedFromCartNotificationModal from './RemovedFromCartNotificationModal';
import {
  selectEnableEditFlow,
  selectHasSubscription,
  selectNicotineStrengths,
} from '../store/selectors';
import pick from 'lodash/fp/pick';
import segment from 'common/segment';

export function PodsPicker({
  error,
  errorCodes,
  removedPods,
  selectedNicotineStrength,
  selectedPods,
  pods,
  podsLimit,
  nicotineStrengths,
  showEditLede,
  updatePodQuantity,
  updateNicotineStrength,
}) {
  const hasQuantity = pod => {
    return selectedPods[pod.id] && selectedPods[pod.id].quantity > 0;
  };

  const [isFlavorGuideModalOpen, setIsFlavorGuideModalOpen] = useState(false);

  const changePodQuantity = pod => {
    return quantity => {
      updatePodQuantity({ pod, quantity });
    };
  };

  const handleModalTrigger = () => {
    segment.track('AS Builder Flavor Guide Viewed');
    setIsFlavorGuideModalOpen(true);
  };

  const PodsShopButton = pod => (
    <>
      {hasQuantity(pod) ? (
        <QuantityInput
          defaultQuantity={0}
          maximum={podsLimit}
          onChange={changePodQuantity(pod)}
          value={selectedPods[pod.id].quantity}
        />
      ) : (
        <ShopButton
          color="light"
          onClick={() => updatePodQuantity({ pod, quantity: 1 })}
        >
          <TranslatedMessage id="plan_builder.add_pod" />
        </ShopButton>
      )}
    </>
  );

  const OutOfStockButton = (
    <ShopButton disabled>
      <TranslatedMessage id="plan_builder.out_of_stock" />
    </ShopButton>
  );

  const CallToAction = pod =>
    pod.is_sold_out ? OutOfStockButton : PodsShopButton(pod);

  const visiblePods = pods.filter(pod => {
    return pod.nicotine_strength_percent === selectedNicotineStrength;
  });

  const podTiles = visiblePods.map(pod => (
    <ProductTile
      key={pod.id}
      callToAction={CallToAction(pod)}
      detail={pod.nicotine_strength_percent}
      imageUrl={`${pod.product_listing_image_url}`}
      name={pod.name}
      price={<PriceSpan cents={pod.price} />}
      starRating={pod.review_score}
    />
  ));

  return (
    <div className="build-your-plan__picker-content">
      <h6>
        <TranslatedMessage
          className="subheading--small"
          id="plan_builder.pod_picker.heading"
        />
      </h6>
      <h1 className="build-your-plan__lede natural-heading">
        <TranslatedMessage
          id={
            showEditLede
              ? 'plan_builder.pod_picker.edit_lede'
              : 'plan_builder.pod_picker.lede'
          }
        />
      </h1>
      <span className="subheading build-your-plan__subheading--green">
        <TranslatedMessage id="plan_builder.pod_picker.subtitle" />
      </span>
      <p className="text--body build-your-plan__intro-copy">
        <TranslatedMessage id="plan_builder.pod_picker.intro_p1" />
      </p>
      <p className="text--body build-your-plan__intro-copy">
        <TranslatedMessage
          id="plan_builder.pod_picker.intro_p2"
          values={{
            flavorGuideModalLink: (
              <a href="#" onClick={handleModalTrigger}>
                <TranslatedMessage id="plan_builder.pod_picker.flavor_modal_link" />
              </a>
            ),
          }}
        />
      </p>
      <hr className="build-your-plan__hr" />
      {!isEmpty(nicotineStrengths) && !isEmpty(selectedNicotineStrength) && (
        <PodsFilter
          nicotineStrengths={nicotineStrengths}
          selectedNicotineStrength={selectedNicotineStrength}
          updateNicotineStrength={updateNicotineStrength}
        />
      )}
      {visiblePods.length === 0 ? (
        <LoadingSplash />
      ) : (
        <ProductListing>{podTiles}</ProductListing>
      )}
      {!!error && <CartErrorModal errorCodes={errorCodes} error={error} />}
      {!isEmpty(removedPods) && (
        <RemovedFromCartNotificationModal pods={removedPods} />
      )}
      <FlavorGuideModal
        pods={pods}
        isOpen={isFlavorGuideModalOpen}
        onRequestClose={() => setIsFlavorGuideModalOpen(false)}
      />
    </div>
  );
}

const mapStateToProps = state => ({
  nicotineStrengths: selectNicotineStrengths(state),
  showEditLede: selectEnableEditFlow(state) && selectHasSubscription(state),
  ...pick(
    [
      'pods',
      'podsLimit',
      'removedPods',
      'selectedPods',
      'selectedNicotineStrength',
      'subscription',
    ],
    state
  ),
});

export default connect(
  mapStateToProps,
  actions
)(PodsPicker);
